import React, { useState } from 'react';
import { GatewayRule, PaymentGatewayId } from '../../types';
import { useSettings } from '../SettingsContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const GATEWAY_LABELS: Record<PaymentGatewayId, string> = {
  stripe: 'Stripe',
  razorpay: 'Razorpay',
  cashfree: 'Cashfree',
  lemonsqueezy: 'Lemon Squeezy',
  paddle: 'Paddle',
  checkoutcom: 'Checkout.com',
  chargebee: 'Chargebee'
};

const SortableRuleRow: React.FC<{ rule: GatewayRule, index: number, onToggle: (id: string) => void }> = ({ rule, index, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: rule.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-slate-50/30 transition-colors group bg-white">
      <td className="px-10 py-6" {...attributes} {...listeners}>
         <div className="flex items-center gap-4 cursor-grab">
            <svg className="w-4 h-4 text-slate-300" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
            <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-400">{index + 1}</span>
            <span className="text-sm font-bold text-slate-900">{GATEWAY_LABELS[rule.gateway]}</span>
         </div>
      </td>
      <td className="px-10 py-6">
         <div className="flex flex-wrap gap-1.5">
            {rule.currencies.map(curr => (
              <span key={curr} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[9px] font-black border border-indigo-100">{curr === '*' ? 'ANY CURR' : curr}</span>
            ))}
            {rule.countries.map(country => (
              <span key={country} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px] font-black border border-slate-200">{country === '*' ? 'ANY COUNTRY' : country}</span>
            ))}
         </div>
      </td>
      <td className="px-10 py-6 text-right">
         <button onClick={() => onToggle(rule.id)} className={`w-12 h-6 rounded-full relative p-1 transition-all ${rule.isEnabled ? 'bg-emerald-400' : 'bg-slate-200'}`}>
            <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${rule.isEnabled ? 'translate-x-6' : 'translate-x-0'}`}></div>
         </button>
      </td>
    </tr>
  );
};

const RoutingRules: React.FC<{ onSave: () => void }> = ({ onSave }) => {
  const { settings, updateSettings } = useSettings();
  const rules = settings.gatewayRules as GatewayRule[];
  const [editingRule, setEditingRule] = useState<GatewayRule | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleToggleGatewayRule = (id: string) => { // Force re-lint
    const rule = rules.find(r => r.id === id);
    const updated = rules.map(r => 
      r.id === id ? { ...r, isEnabled: !r.isEnabled } : r
    );
    updateSettings({ gatewayRules: updated });
    saveRules(updated, 'Rule toggled', `${GATEWAY_LABELS[rule!.gateway]}: ${rule!.isEnabled ? 'Disabled' : 'Enabled'}`);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = rules.findIndex(r => r.id === active.id);
      const newIndex = rules.findIndex(r => r.id === over.id);
      const updatedRules = arrayMove(rules, oldIndex, newIndex);
      updateSettings({ gatewayRules: updatedRules });
      saveRules(updatedRules, 'Rules reordered', 'Gateway Rules');
    }
  };

  const handleSaveRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRule) return;
    
    // Conflict check
    const conflict = rules.find(r => 
      r.id !== editingRule.id && 
      r.gateway === editingRule.gateway &&
      r.currencies.every(c => editingRule.currencies.includes(c)) &&
      r.countries.every(c => editingRule.countries.includes(c))
    );
    if (conflict) {
      alert('A rule with the same gateway, currencies, and countries already exists.');
      return;
    }

    let updatedRules;
    const isNew = !rules.find(r => r.id === editingRule.id);
    if (isNew) {
      updatedRules = [...rules, { ...editingRule, id: `gr-${Date.now()}` }];
    } else {
      updatedRules = rules.map(r => r.id === editingRule.id ? editingRule : r);
    }
    updateSettings({ gatewayRules: updatedRules });
    setEditingRule(null);
    setIsModalOpen(false);
    await saveRules(updatedRules, isNew ? 'Rule added' : 'Rule updated', `${GATEWAY_LABELS[editingRule.gateway]}: ${editingRule.currencies.join(',')} in ${editingRule.countries.join(',')}`);
  };

  const saveRules = async (rules: GatewayRule[], action?: string, ruleDescription?: string) => {
    try {
      const response = await fetch('/api/gateway-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules, action, ruleDescription })
      });
      if (!response.ok) throw new Error('Failed to save rules');
      onSave();
    } catch (error) {
      console.error('Failed to save rules:', error);
      alert('Failed to save rules. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
           <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 15h2a2 2 0 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17"/><path d="m7 21 1.6-4.5c.3-.8 1-1.5 1.8-1.5h7.6c.8 0 1.5.7 1.8 1.5L21 21"/></svg>
           </div>
           <div>
             <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Smart Gateway Routing</h2>
             <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-0.5 italic">Drag to reorder priority</p>
           </div>
        </div>
        <button 
          onClick={() => {
            setEditingRule({ id: '', gateway: 'stripe', currencies: ['*'], countries: ['*'], isEnabled: true });
            setIsModalOpen(true);
          }}
          className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black shadow-lg"
        >
          Add Rule
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rules.map(r => r.id)} strategy={verticalListSortingStrategy}>
                <tbody className="divide-y divide-slate-100">
                  {rules.map((rule, index) => (
                    <SortableRuleRow key={rule.id} rule={rule} index={index} onToggle={handleToggleGatewayRule} />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>
      </div>

      {isModalOpen && editingRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden p-10 animate-in zoom-in-95">
             <h2 className="text-2xl font-black text-slate-900 mb-6">Configure Routing</h2>
             <form onSubmit={handleSaveRule} className="space-y-6">
                <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-bold" value={editingRule.gateway} onChange={(e) => setEditingRule({ ...editingRule, gateway: e.target.value as PaymentGatewayId })}>
                   {Object.entries(GATEWAY_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                <input 
                   type="text" 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-bold" 
                   placeholder="Currencies (comma separated, * for all)"
                   value={editingRule.currencies.join(', ')} 
                   onChange={(e) => setEditingRule({ ...editingRule, currencies: e.target.value.split(',').map(s => s.trim()) })}
                />
                <input 
                   type="text" 
                   className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-bold" 
                   placeholder="Countries (comma separated, * for all)"
                   value={editingRule.countries.join(', ')} 
                   onChange={(e) => setEditingRule({ ...editingRule, countries: e.target.value.split(',').map(s => s.trim()) })}
                />
                <div className="flex gap-4">
                   <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-4 text-xs font-black text-slate-500 uppercase tracking-widest">Cancel</button>
                   <button type="submit" className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100">Save</button>
                </div>
             </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutingRules;
